'use client';

import { forwardRef, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Components ---
import { TableInsertGridPanel } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/table-insert-grid-panel';
import type { UseTableTriggerButtonConfig } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/use-table-trigger';
import { useTableTriggerButton } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-trigger-button/use-table-trigger';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/tiptap-editor/tiptap-ui-primitive/popover';

// --- Styles ---
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';

export interface TableTriggerButtonProps extends Omit<ButtonProps, 'type'>, UseTableTriggerButtonConfig {
    /**
     * Optional text to display alongside the icon.
     */
    text?: string;
}

/**
 * Button component for inserting tables in a Tiptap editor with a grid selector.
 *
 * For custom button implementations, use the `useTableTriggerButton` hook instead.
 *
 * @example
 * ```tsx
 * <TableTriggerButton
 *   editor={editor}
 *   hideWhenUnavailable={true}
 *   onInserted={(rows, cols) => console.log(`Inserted ${rows}x${cols} table`)}
 * />
 * ```
 */
export const TableTriggerButton = forwardRef<HTMLButtonElement, TableTriggerButtonProps>(
    (
        {
            editor: providedEditor,
            hideWhenUnavailable = false,
            mode = 'table',
            maxRows = 8,
            maxCols = 8,
            onInserted,
            text,
            children,
            ...buttonProps
        },
        ref,
    ) => {
        const { editor } = useTiptapEditor(providedEditor);
        const [isOpen, setIsOpen] = useState(false);
        const { isVisible, canInsert, label, Icon } = useTableTriggerButton({
            editor: providedEditor,
            hideWhenUnavailable,
            mode,
            maxRows,
            maxCols,
            onInserted,
        });

        if (!isVisible) {
            return null;
        }

        return (
            <Popover
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <PopoverTrigger asChild>
                    <Button
                        ref={ref}
                        type="button"
                        variant="ghost"
                        disabled={!canInsert}
                        data-disabled={!canInsert}
                        aria-label={label}
                        tooltip={label}
                        {...buttonProps}
                    >
                        {children ?? (
                            <>
                                <Icon className="tiptap-button-icon" />
                                {text && <span className="tiptap-button-text">{text}</span>}
                            </>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    side="bottom"
                >
                    <Card>
                        <CardBody>
                            <TableInsertGridPanel
                                editor={editor}
                                mode={mode}
                                maxRows={maxRows}
                                maxCols={maxCols}
                                focusInputs={isOpen}
                                onInserted={(rows, cols) => {
                                    setIsOpen(false);
                                    onInserted?.(rows, cols);
                                }}
                            />
                        </CardBody>
                    </Card>
                </PopoverContent>
            </Popover>
        );
    },
);

TableTriggerButton.displayName = 'TableTriggerButton';
